import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/mergeMap';
import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { TemperatureConverterService } from '../../common/temperature-converter.service';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';

import { INumericValue } from '../../interfaces/numeric-value.interface';
import { ITextValue } from '../../interfaces/text-value.interface';
import { IListValue } from '../../interfaces/list-value.interface';
import { WaterHeaterModes } from './water-heater-modes.enum';
import { take } from 'rxjs/operators';

const CACHE_TIME: number = 3600;
const DIVISOR: number = 1;
const MULTIPLIER: number = 1;

export const WATER_HEATER_MODES = [
  {name: 'Disabled', value: 0, translation: 'AR.CONDITIONS_NL.DISABLED'},
  {name: 'Enabled', value: 1, translation: 'AR.CONDITIONS_NL.ENABLED'},
  {name: 'Energy Saver', value: 2, translation: 'AR.CONDITIONS_NL.ENERGY_SAVER'},
  {name: 'High Demand', value: 3, translation: 'AR.CONDITIONS_NL.HIGH_DEMAND'},
  {name: 'Heat Pump Only', value: 4, translation: 'AR.CONDITIONS_NL.HEAT_PUMP_ONLY'},
  {name: 'Electric-Only', value: 5, translation: 'AR.CONDITIONS_NL.ELECTRIC_ONLY'},
  {name: 'Performance', value: 6, translation: 'AR.CONDITIONS_NL.PERFORMANCE'},
  {name: 'Vacation', value: 7, translation: 'AR.CONDITIONS_NL.VACATION'},
  {name: 'Electric', value: 8, translation: 'AR.CONDITIONS_NL.ELECTRIC'},
  {name: 'Heat Pump', value: 9, translation: 'AR.CONDITIONS_NL.HEAT_PUMP'},
  {name: 'Not Set', value: 0xff, translation: 'AR.CONDITIONS_NL.NOT_SET'}
];

export const WH_TYPES = [
  {type: 'Gas Water Heater', modes: []},
  {type: 'Commercial Hi Eff. Water Heater', modes: [1, 0]},
  {type: 'Heat Pump Water Heater ', modes: [2, 4, 3, 5, 7]},
  {type: 'Heat Pump Water Heater Gen 4', modes: [2, 9, 3, 8]},
  {type: 'Tankless Water Heater', modes: []},
  {type: 'Electric Water Heater', modes: [2, 6]}
];

export class WaterHeaterServerPlugin extends SmartenitPlugin
  implements INumericValue, ITextValue, IListValue {
  private _mode: any;
  private _setPoint: number;
  private _ambientTemperature: number;
  private _avgTemperature: number;
  private _energy: number;
  private _power: number;

  public getReportableAttrs() {
    return [];
  }

  public setMode(mode: WaterHeaterModes, subscribe?: boolean): any {
    this._mode = mode;

    const obs = this.setAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'SystemMode',
      { value: mode },
      subscribe || false
    );

    if (obs) {
      obs.pipe(take(1))
        .subscribe(
          (res) => {
            this._onUpdate.next({ SetPoint: this._mode });
            const message: IWebSocketDeviceMessage = {
              attributeOrMethod: 'SystemMode',
              componentId: this.componentId,
              data: { response: { value: this._mode } },
              processorName: this.processorName,
              resource: 'devices',
              resourceId: this.device._id
            };
            this.device.webSocketsService.proxyMessage(message);
          },
          (err) => this._onUpdate.next({ SystemMode: this._mode, error: err }));
      return obs;
    }
  }

  public getValue(attribute?: string): number {
    let value: any = null;
    switch (attribute) {
      case 'SetPoint':
        value = this._setPoint;
        break;
      case 'SystemMode':
        value = this._mode;
        break;
      case 'AmbientTemperature':
        value = this._ambientTemperature;
        break;
      case 'Power':
        value = this._power;
        break;
      case 'Energy':
        value = this._energy;
        break;
      case 'AverageTemperature':
        value = this._avgTemperature;
        break;
    }

    return value;
  }

  public getUnit(attribute?: string): string {
    let value: string = '';

    switch (attribute) {
      case 'SetPoint':
        value = this.temperatureConverterService.getUnit();
        break;
      case 'AmbientTemperature':
        value = this.temperatureConverterService.getUnit();
        break;
      case 'AverageTemperature':
        value = this.temperatureConverterService.getUnit();
        break;
      case 'SystemMode':
        value = '';
        break;
      case 'Power':
        value = 'kW';
        break;
      case 'Energy':
        value = 'kWh';
        break;
    }

    return value;
  }

  private getCurrentPower() {
    return this._power ? this._power : '-';
  }

  private getCurrentEnergy() {
    return this._energy ? this._energy : '-';
  }

  public getTextValue(context?: string): string {
    const temperatureUnit = this.temperatureConverterService.getUnit();
    if (context === 'displayEnergyAndPower') {
      return `${this.getCurrentPower()} W / ${this.getCurrentEnergy()} kWh`;
    } else if (context === 'ambientTemperature') {
      let temperature = Math.floor(this._ambientTemperature);
      temperature = this._ambientTemperature
        ? Math.floor(
          this.temperatureConverterService.convert(
            temperature, 'C'
          )
        )
        : temperature;
      return this._ambientTemperature
        ? `${temperature.toString()}${temperatureUnit}`
        : '';
    } else if (context === 'setPoint') {
      let temperature = Math.floor(this._setPoint);
      temperature = this._setPoint
        ? Math.floor(
          this.temperatureConverterService.convert(
            temperature,
            TemperatureConverterService.DEFAULT_TEMPERATURE
          )
        )
        : temperature;
      return this._setPoint
        ? `${temperature.toString()}${temperatureUnit}`
        : '';
    } else {
      let temperature = Math.floor(this._avgTemperature);
      temperature = this._avgTemperature
        ? Math.floor(
          this.temperatureConverterService.convert(
            temperature,
            TemperatureConverterService.DEFAULT_TEMPERATURE
          )
        )
        : temperature;
      return this._avgTemperature
        ? `${temperature.toString()}${temperatureUnit}`
        : '';
    }
  }

  public formatValue(value: string): string {
    if (value != null && !isNaN(Number(value))) {
      const temperature = Math.round(
        this.temperatureConverterService.convert(
          Number(value),
          TemperatureConverterService.DEFAULT_TEMPERATURE
        )
      );
      return `${temperature.toString()}`;
    } else {
      return value;
    }
  }

  public getConditionAttribute(): string {
    return 'SystemMode';
  }

  public getConditionOptions(): any {
    const modeData = WH_TYPES.find((m) => m.type === this.device.model);
    if (!modeData) {
      return [];
    }
    return modeData.modes.map((mode) => {
      const waterHeaterMode = WATER_HEATER_MODES.find((m) => m.value == mode);
      console.log({waterHeaterMode});
      return  {
        displayName: _.get(
          this.translationService.getResource(),
          waterHeaterMode ? waterHeaterMode.translation : '',
          ''
        ),
        name: waterHeaterMode ? waterHeaterMode.name : '',
        value: waterHeaterMode ? waterHeaterMode.value : ''
      };
    });
  }

  public getEffectOptions(): any {
    // later include values for the zigbee enabled devices
    const modeData = WH_TYPES.find((m) => m.type === this.device.meta.subType);
    if (!modeData) {
      return [];
    }
    return modeData.modes.map((mode) => {
      const waterHeaterMode = WATER_HEATER_MODES.find((m) => m.value == mode);
      return  {
        displayName: _.get(
          this.translationService.getResource(),
          waterHeaterMode ? waterHeaterMode.translation : '',
          ''
        ),
        name: waterHeaterMode ? waterHeaterMode.name : '',
        value: waterHeaterMode ? waterHeaterMode.value : ''
      };
    });
  }

  public getEffectAttribute(): string {
    return 'SystemMode';
  }

  public getEffectMethod(context?: any): string | null {
    return null;
  }

  public setValue(value: number, attribute?: string, subscribe?: boolean): any {
    switch (attribute) {
      case 'SetPoint':
        this._setPoint = value;
        const obs = this.setAttributeWithSubscribeOption(
          this.componentId,
          this.processorName,
          'SetPoint',
          { value: this._setPoint },
          subscribe || false
        );
        if (obs) {
          obs.pipe(take(1))
            .subscribe(
              (res) => {
                this._onUpdate.next({ SetPoint: this._setPoint });
                const message: IWebSocketDeviceMessage = {
                  attributeOrMethod: 'SetPoint',
                  componentId: this.componentId,
                  data: { response: { value: this._setPoint } },
                  processorName: this.processorName,
                  resource: 'devices',
                  resourceId: this.device._id
                };
                this.device.webSocketsService.proxyMessage(message);
              },
              (err) => this._onUpdate.next({ SetPoint: this._setPoint, error: err }));
          return obs;
        }
        break;
    }
  }

  public getStatus(
    context?: string,
    force?: boolean,
    subscribe?: boolean,
    payload?: any,
    sendDirect: boolean = false
  ): void {
    if (context === 'SetPoint') {
      this.getCache('SetPoint').pipe(take(1)).subscribe((cached: any) => {
        if (cached == null) {
          this.getAttributeWithSubscribeOption(
            this.componentId,
            this.processorName,
            'SetPoint',
            null,
            false,
            `cacheTTL=${CACHE_TIME}`
          );
        } else {
          this._setPoint = cached;
          this._onUpdate.next({
            SetPoint: this._setPoint
          });
        }
      });
    } else if (context === 'SystemMode') {
      this.getCache('SystemMode').pipe(take(1)).subscribe((cached: any) => {
        if (cached == null) {
          this.getAttributeWithSubscribeOption(
            this.componentId,
            this.processorName,
            'SystemMode',
            null,
            false,
            `cacheTTL=${CACHE_TIME}`
          );
        } else {
          this._mode = cached;
          this._onUpdate.next({
            SystemMode: this._mode
          });
        }
      });
    } else if (context === 'AverageTemperature') {
      this.getCache('AverageTemperature').pipe(take(1)).subscribe((cached: any) => {
        if (cached == null) {
          this.getAttributeWithSubscribeOption(
            this.componentId,
            this.processorName,
            'AverageTemperature',
            null,
            false,
            `cacheTTL=${CACHE_TIME}`
          );
        } else {
          this._avgTemperature = cached;
          this._onUpdate.next({ AverageTemperature: this._avgTemperature });
        }
      });
    } else {
      super.getStatus(context, force, subscribe, payload, sendDirect);
    }
    this.device.onDeviceResponse.pipe(take(1)).subscribe((r) => {
      // NEEEDED TO TRIGGER API INMEDIATE RESPONSE FROM RHEEM-API-DEVICES
      // if the api responds with data inmediatly proxy the message to processMessage
      // to mimic the mqtt response from the devices
      if (r.response.data !== {}) {
        const keys = Object.keys(r.response.data);
        keys.forEach((k) => {
          const message: IWebSocketDeviceMessage = {
            attributeOrMethod: k,
            componentId: this.componentId,
            data: { response: { value: r.response.data[k] } },
            processorName: this.processorName,
            resource: 'devices',
            resourceId: this.device._id
          };
          const p: any = {};
          p[k] = r.response.data[k];
          this._onUpdate.next(p);
          this.device.webSocketsService.proxyMessage(message);
        });
      }
    });
  }

  public getSelectedValues(): any[] {
    return [this._mode];
  }

  public selectValue(option: any, subscribe?: boolean): any {
    this._mode = option;
    this._onUpdate.next({ SystemMode: this._mode });

    return this.setMode(option, true);
  }

  public getCachedValues(subscribe?: boolean): any {
    const cacheValues = [
      this.getCache('SetPoint'),
      this.getCache('SystemMode'),
      this.getCache('AverageTemperature'),
      this.getCache('AmbientTemperature'),
      this.getCache('Power'),
      this.getCache('Energy')
    ];

    const observable = Observable.forkJoin(cacheValues).flatMap((array) => {
      if (array && array[0] != null) {
        this._setPoint = array[0];
      }

      if (array && array[1] != null) {
        this._mode = array[1];
      }

      if (array && array[2] != null) {
        this._avgTemperature = array[2];
      }

      if (this._avgTemperature != null) {
        this.device.cached = true;
      }

      this._onUpdate.next({
        AverageTemperature: this._avgTemperature,
        SetPoint: this._setPoint,
        SystemMode: this._mode
      });

      return Observable.of(this._avgTemperature);
    });

    if (subscribe === true) {
      return observable;
    }

    const subscription: Subscription = observable.subscribe(() =>
      subscription.unsubscribe()
    );
  }

  public getStatusPayload(): any {
    return [
      'AmbientTemperature',
      'SystemMode',
      'SetPoint',
      'AverageTemperature',
      'Power',
      'Energy'
    ];
  }

  public processMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const attribute = message && message.attributeOrMethod;
    if (attribute === 'AverageTemperature') {
      const response = message && message.data && message.data.response;

      this._avgTemperature = response.value / DIVISOR;
      this.state = this._avgTemperature;

      this._onUpdate.next({ AverageTemperature: this._avgTemperature });
    } else if (attribute === 'SystemMode') {
      const response = message && message.data && message.data.response;
      this._mode = parseInt(response.value);
      this.setCache('SystemMode', this._mode, CACHE_TIME, undefined, timestamp);
      this._onUpdate.next({ SystemMode: this._mode });
    } else if (attribute === 'SetPoint') {
      const response = message && message.data && message.data.response;
      this._setPoint = response.value / DIVISOR;
      this.setCache(
        'SetPoint',
        this._setPoint,
        CACHE_TIME,
        undefined,
        timestamp
      );
      this._onUpdate.next({ SetPoint: this._setPoint });
    } else if (attribute === 'AmbientTemperature') {
      const response = message && message.data && message.data.response;

      this._ambientTemperature = response.value / DIVISOR;

      this.setCache(
        'AmbientTemperature',
        this._ambientTemperature,
        CACHE_TIME,
        undefined,
        timestamp
      );

      this._onUpdate.next({ AmbientTemperature: this._ambientTemperature });
    } else if (attribute === 'Power') {
      const response = message && message.data && message.data.response;

      this._power = response.value / DIVISOR;

      this.setCache(
        'Power',
        this._avgTemperature,
        CACHE_TIME,
        undefined,
        timestamp
      );

      this._onUpdate.next({
        Power: this._power
      });
    } else if (attribute === 'Energy') {
      const response = message && message.data && message.data.response;

      this._energy = response.value / DIVISOR;

      this.setCache(
        'Energy',
        this._avgTemperature,
        CACHE_TIME,
        undefined,
        timestamp
      );

      this._onUpdate.next({
        Energy: this._energy
      });
    }

    return this.state;
  }

  public getRawValue(
    value: number,
    attribute?: string,
    method?: string
  ): number {
    if (
      !attribute ||
      attribute === 'AverageTemperature' ||
      attribute === 'SetPoint' ||
      attribute === 'AmbientTemperature' ||
      attribute === 'Power' ||
      attribute === 'Energy'
    ) {
      return this.pluginUtilsService.getValueByMultiplying(value, MULTIPLIER);
    } else {
      return value;
    }
  }

  public getProcessedValue(value: any, attribute?: string, method?: string) {
    if (
      !attribute ||
      attribute === 'AverageTemperature' ||
      attribute === 'SetPoint' ||
      attribute === 'AmbientTemperature' ||
      attribute === 'Power' ||
      attribute === 'Energy'
    ) {
      return this.pluginUtilsService.getValueByDividing(value, DIVISOR);
    } else {
      return value;
    }
  }
}
